import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatJobIdForRoute } from '../services/routingAdapter';
import globalConfig from '../utils/config'; // Import global config for API URLs
import { snowflakeApi } from '../utils/api';
import {
  Paper,
  Typography,
  Box,
  TextField,
  Button as MuiButton,
  IconButton,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid as MuiGrid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip,
  Container as MuiContainer,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoFixHigh as AutoFixHighIcon,
  Storage as DatabaseIcon,
  PlayArrow as PlayArrowIcon,
  Home as HomeIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { 
  Module4Service, 
  ClientConfig, 
  PromptConfig, 
  ResultsConfig, 
  useCreateConfigDb, 
  useUpdateConfigDb, 
  useGetModuleConfigDb, 
  useSaveModuleConfigDb,
  DbBatchProcessRequest,
  DbBatchProcessResponse,
  DbModuleConfigResponse
} from '../services/module4Service';
import { useAppContext } from '../context/AppContext';
import { useDatabaseMode } from '../context/DatabaseModeContext';
import Module4ConfigAssistant from '../components/Module4ConfigAssistant';
import { workflowService } from '../services/api/workflowService';
import WorkflowStepper from '@/components/WorkflowStepper';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import { clientService } from '../services/api/clientService';

import TabNavigation from '@/components/module4/TabNavigation';
import TabPanel from '@/components/module4/TabPanel';
import TabPanelBasicInfo from '@/components/module4/TabPanelBasicInfo';
import TabPanelTopics from '@/components/module4/TabPanelTopics';
import TabPanelPrompt from '@/components/module4/TabPanelPrompt';
import TabPanelResults from '@/components/module4/TabPanelResults';
import TabPanelProcess from '@/components/module4/TabPanelProcess';
import NavigationBar from '@/components/module4/NavigationBar';

// Interface for CSV file metadata including record counts
interface CsvFileMetadata {
  path: string;
  name: string;
  size?: number;
  lastModified?: string;
  record_count?: number | null;
}

const Module4Config: React.FC = () => {
  const { clientId, workflowId: urlWorkflowId, configId } = useParams();
  const navigate = useNavigate();
  const { setError } = useAppContext();
  const { isUsingDatabaseApproach } = useDatabaseMode();
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAssistant, setShowAssistant] = useState(configId === 'new');
  
  // Database approach is now managed by context
  const [dbClientId, setDbClientId] = useState<number | null>(() => {
    // Try to load from localStorage
    const storedId = localStorage.getItem('db_client_id');
    return storedId ? parseInt(storedId, 10) : null;
  });
  const [dbConfigId, setDbConfigId] = useState<number | null>(() => {
    // Try to load from localStorage if we're not creating a new config
    if (configId && configId !== 'new') {
      const storedId = localStorage.getItem(`module4_${configId}_db_config_id`);
      return storedId ? parseInt(storedId, 10) : null;
    }
    return null;
  });
  
  // For workflow route, store the module ID for showing configuration
  const [activeModuleId, setActiveModuleId] = useState<number | null>(null);
  
  // Database-first React Query hooks
  const createConfigDbMutation = useCreateConfigDb();
  const updateConfigDbMutation = useUpdateConfigDb();
  const saveModuleConfigDbMutation = useSaveModuleConfigDb();
  const moduleConfigQuery = useGetModuleConfigDb(activeModuleId || 0);
  
  // Add debounce mechanism to prevent connection pool exhaustion
  const [saveDebounceTimer, setSaveDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Cleanup any pending timers when component unmounts
  useEffect(() => {
    return () => {
      if (saveDebounceTimer) {
        clearTimeout(saveDebounceTimer);
      }
    };
  }, [saveDebounceTimer]);

  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (clientId && /^\d+$/.test(clientId)) {
        try {
          const client = await clientService.getClient(parseInt(clientId, 10));
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };

    fetchClientData();
  }, [clientId]);
  
  // Handle database query errors
  useEffect(() => {
    if (moduleConfigQuery.isError) {
      console.error('Database query error:', moduleConfigQuery.error);
      setSnackbar({
        open: true,
        message: 'Failed to load configuration. Database connection issue.',
        severity: 'error'
      });
    }
  }, [moduleConfigQuery.isError]);
  
  // Add error handling effect for mutations
  useEffect(() => {
    const handleMutationError = (error: unknown, operation: string) => {
      console.error(`${operation} failed:`, error);
      setSnackbar({
        open: true,
        message: 'Database connection error. Please try again later.',
        severity: 'error'
      });
    };
    
    if (createConfigDbMutation.isError) {
      handleMutationError(createConfigDbMutation.error, 'Create config');
    }
    
    if (updateConfigDbMutation.isError) {
      handleMutationError(updateConfigDbMutation.error, 'Update config');
    }
    
    if (saveModuleConfigDbMutation.isError) {
      handleMutationError(saveModuleConfigDbMutation.error, 'Save module config');
    }
  }, [
    createConfigDbMutation.isError, createConfigDbMutation.error,
    updateConfigDbMutation.isError, updateConfigDbMutation.error,
    saveModuleConfigDbMutation.isError, saveModuleConfigDbMutation.error
  ]);
  
  // Form state
  const [config, setConfig] = useState<ClientConfig>({
    config_id: '',
    name: '',
    description: '',
    topic_mapping: {},
    intent_mapping: {},
    funnel_mapping: {},
    subtopic_mappings: {},
    topic_validations: {},
    prompt: {
      system_prompt: '',
      output_format: '',
      temperature: 0.7,
      max_tokens: 1000
    },
    results: {
      output_format: '',
      output_regex: '',
      result_columns: []
    },
    version: 1,
    is_active: true
  });

  // Add state for WorkflowStepper collapse
  const [stepperCollapsed, setStepperCollapsed] = useState(() => {
    return localStorage.getItem('module4_stepper_collapsed') === 'true';
  });
  
  // Batch process state
  const [inputFilePath, setInputFilePath] = useState('');
  const [csvFiles, setCsvFiles] = useState<CsvFileMetadata[]>([]);
  const [isLoadingCsvFiles, setIsLoadingCsvFiles] = useState(false);
  const [selectedModel, setSelectedModel] = useState('nano'); // Default to nano
  const [batchSize, setBatchSize] = useState<number | undefined>(undefined);
  const [priority, setPriority] = useState<number | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchSuccessMessage, setBatchSuccessMessage] = useState<string | null>(null);
  const [clientData, setClientData] = useState<any>(null);

  // Fetch CSV files for the dropdown with debounce
  const [fetchCsvDebounceTimer, setFetchCsvDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  const fetchCsvFiles = async () => {
    // Clear any existing debounce timer
    if (fetchCsvDebounceTimer) {
      clearTimeout(fetchCsvDebounceTimer);
    }
    
    // Set a new timer to prevent rapid consecutive API calls
    const timer = setTimeout(async () => {
      try {
        setIsLoadingCsvFiles(true);
        // Build dynamic URL based on current clientId and workflowId
        const clientIdParam = clientId || (config.config_id ? config.config_id.split('-')[0] : '');
        const workflowIdParam = urlWorkflowId || '';
        
        if (!clientIdParam || !workflowIdParam) {
          console.warn('Missing clientId or workflowId for CSV files fetch');
          return;
        }
        
        // Prepare params for both module2 output and original uploads directories with record counts
        const module2Params = {
          container: 'client-data',
          path: `${clientIdParam}/workflows/${workflowIdParam}/module2/output`,
          add_client_prefix: false,
          client_id: clientIdParam,
          include_record_count: true,
        } as const;
        const uploadsParams = {
          container: 'client-data',
          path: `${clientIdParam}/workflows/${workflowIdParam}/uploads`,
          add_client_prefix: false,
          client_id: clientIdParam,
          include_record_count: true,
        } as const;

        console.log("Fetching CSV files from module2 output with params:", module2Params);
        console.log("Fetching CSV files from uploads with params:", uploadsParams);

        // Fetch from both locations in parallel using axios client with auth interceptor
        const [module2Response, uploadsResponse] = await Promise.allSettled([
          snowflakeApi.get('/api/v1/storage/list', { params: module2Params }),
          snowflakeApi.get('/api/v1/storage/list', { params: uploadsParams }),
        ]);
        
        let csvFilesList: CsvFileMetadata[] = [];
        
        // Process module2 output files (existing logic)
        if (module2Response.status === 'fulfilled' && module2Response.value.status >= 200 && module2Response.value.status < 300) {
          const module2Data = module2Response.value.data;
          console.log("Module2 API response:", module2Data);
          
          if (module2Data && module2Data.files) {
            const module2Files = Array.isArray(module2Data.files) ? module2Data.files : [];
            const processedModule2Files = module2Files
              .filter((file: any) => {
                const fileName = (typeof file === 'string' ? file : (file.name || file.path || '')).toLowerCase();
                return fileName.endsWith('.csv') && fileName.includes('relevant');
              })
              .map((file: any) => {
                let fullPath = typeof file === 'string' ? file : (file.path || file.name);
                if (fullPath && !fullPath.startsWith('client-data/')) {
                  fullPath = `client-data/${fullPath}`;
                }
                return {
                  path: fullPath,
                  name: typeof file === 'string' ? file : (file.name || file.path || ''),
                  size: file.size,
                  lastModified: file.lastModified,
                  record_count: file.record_count
                };
              });
            
            csvFilesList.push(...processedModule2Files);
          }
        } else {
          console.warn('Failed to fetch module2 output files:', module2Response);
        }
        
        // Process uploads files (new functionality for original keyword files)
        if (uploadsResponse.status === 'fulfilled' && uploadsResponse.value.status >= 200 && uploadsResponse.value.status < 300) {
          const uploadsData = uploadsResponse.value.data;
          console.log("Uploads API response:", uploadsData);
          
          if (uploadsData && uploadsData.files) {
            const uploadsFiles = Array.isArray(uploadsData.files) ? uploadsData.files : [];
            const processedUploadsFiles = uploadsFiles
              .filter((file: any) => {
                const fileName = (typeof file === 'string' ? file : (file.name || file.path || '')).toLowerCase();
                return fileName.endsWith('.csv');
              })
              .map((file: any) => {
                let fullPath = typeof file === 'string' ? file : (file.path || file.name);
                if (fullPath && !fullPath.startsWith('client-data/')) {
                  fullPath = `client-data/${fullPath}`;
                }
                return {
                  path: fullPath,
                  name: typeof file === 'string' ? file : (file.name || file.path || ''),
                  size: file.size,
                  lastModified: file.lastModified,
                  record_count: file.record_count
                };
              });
            
            csvFilesList.push(...processedUploadsFiles);
          }
        } else {
          console.warn('Failed to fetch uploads files:', uploadsResponse);
        }
        
        // Remove duplicates and sort
        csvFilesList = [...new Set(csvFilesList)];
        csvFilesList.sort((a, b) => {
          // Sort with module2 relevant files first (latest first), then uploads files (latest first)
          const aIsRelevant = a.name.toLowerCase().includes('relevant');
          const bIsRelevant = b.name.toLowerCase().includes('relevant');
          
          if (aIsRelevant && !bIsRelevant) return -1;
          if (!aIsRelevant && bIsRelevant) return 1;
          
          // Within same category, sort by filename (reverse for latest first)
          return b.name.localeCompare(a.name);
        });
        
        console.log("Combined and sorted CSV files:", csvFilesList);
        
        // Use hardcoded demo data if nothing was found
        if (csvFilesList.length === 0) {
          csvFilesList = [
            { path: "demo_file1.csv", name: "demo_file1.csv", size: 100, lastModified: "2023-10-27T10:00:00Z", record_count: 100 },
            { path: "demo_file2.csv", name: "demo_file2.csv", size: 200, lastModified: "2023-10-27T11:00:00Z", record_count: 200 },
            { path: "module1_output.csv", name: "module1_output.csv", size: 50, lastModified: "2023-10-27T12:00:00Z", record_count: 50 }
          ];
          console.log("No CSV files found, using demo data");
        }
        
        setCsvFiles(csvFilesList);
      } catch (error) {
        console.error('Error fetching CSV files:', error);
        
        // Set hardcoded demo data as fallback
        setCsvFiles([
          { path: "fallback_file1.csv", name: "fallback_file1.csv", size: 100, lastModified: "2023-10-27T10:00:00Z", record_count: 100 },
          { path: "fallback_file2.csv", name: "fallback_file2.csv", size: 200, lastModified: "2023-10-27T11:00:00Z", record_count: 200 },
          { path: "fallback_module1_output.csv", name: "fallback_module1_output.csv", size: 50, lastModified: "2023-10-27T12:00:00Z", record_count: 50 }
        ]);
        
        setSnackbar({
          open: true,
          message: 'Failed to load CSV files, using sample data',
          severity: 'error'
        });
      } finally {
        setIsLoadingCsvFiles(false);
        setFetchCsvDebounceTimer(null);
      }
    }, 300); // 300ms debounce
    
    setFetchCsvDebounceTimer(timer);
  };
  
  // Load configuration if editing an existing config
  useEffect(() => {
    const loadConfig = async () => {
      if (configId && configId !== 'new') {
        setIsLoading(true);
        try {
          // Try to load using database-first approach if enabled and dbConfigId exists
          if (isUsingDatabaseApproach && dbConfigId) {
            try {
              // Load the config using database ID
              const module4Service = Module4Service.getInstance();
              const dbConfig = await module4Service.getConfigDb(dbConfigId);
              
              // Map the DB config to the client config format
              const mappedConfig: ClientConfig = {
                config_id: configId,
                name: dbConfig.name,
                description: dbConfig.description,
                topic_mapping: dbConfig.topicMapping,
                intent_mapping: dbConfig.intentMapping,
                funnel_mapping: dbConfig.funnelMapping,
                subtopic_mappings: dbConfig.subtopicMappings,
                topic_validations: dbConfig.topicValidations,
                prompt: dbConfig.prompt,
                results: dbConfig.results,
                version: dbConfig.version,
                is_active: dbConfig.isActive,
                created_at: dbConfig.createdAt,
                updated_at: dbConfig.updatedAt
              };
              
              setConfig(mappedConfig);
              setDbClientId(dbConfig.clientId);
              localStorage.setItem('db_client_id', dbConfig.clientId.toString());
              
              console.log('Loaded config using database-first approach:', dbConfig);
              return;
            } catch (dbError) {
              console.error('Error loading config with database approach:', dbError);
              // Fall through to standard approach
            }
          }
          
          // Standard blob-first approach
          const module4Service = Module4Service.getInstance();
          const loadedConfig = await module4Service.getConfig(configId);
          setConfig(loadedConfig);
        } catch (error) {
          console.error('Error loading batch keyword classification:', error);
          setSnackbar({
            open: true,
            message: 'Failed to load batch keyword classification',
            severity: 'error'
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadConfig();
  }, [configId, isUsingDatabaseApproach, dbConfigId]);
  
  // Load config for workflow route
  useEffect(() => {
    // If we have clientId and urlWorkflowId but no configId, we're in the workflow route
    if (clientId && urlWorkflowId && !configId && isUsingDatabaseApproach) {
      const fetchWorkflowModules = async () => {
        try {
          setIsLoading(true);
          
          // Always fetch workflow details to get the latest module information
          const workflow = await workflowService.getDbWorkflow(Number(urlWorkflowId));
          const modules = (workflow as any).modules || [];
          
          // Filter for module4-config type modules
          const module4ConfigModules = modules.filter((m: any) => m.module_type === 'module4-config');
          
          if (module4ConfigModules.length > 0) {
            // Sort by module_id descending to get the latest one
            // (Assuming higher module_id means newer; or use created_at if available and reliable)
            module4ConfigModules.sort((a: any, b: any) => {
              if (b.created_at && a.created_at) {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              }
              return (b.module_id || 0) - (a.module_id || 0); // Fallback to module_id
            });
            
            const latestModule4Config = module4ConfigModules[0];
            setActiveModuleId(latestModule4Config.module_id);
            console.log(`Set activeModuleId to latest module4-config: ${latestModule4Config.module_id} for workflow ${urlWorkflowId}`);
            
            // Optional: If you still want to store the LATEST discovered ID for other purposes, you can do it here.
            // However, for determining the active config on this page load, we rely on the fresh fetch.
            // localStorage.setItem(`workflow_${urlWorkflowId}_latest_module4_config_id`, latestModule4Config.module_id.toString());
          } else {
            setActiveModuleId(null); // No module4-config found for this workflow
            console.log(`No module4-config module found for workflow ${urlWorkflowId}`);
          }
        } catch (error) {
          console.error('Error loading workflow modules:', error);
          setActiveModuleId(null);
          // Potentially set an error state to inform the user
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchWorkflowModules();
    }
    // Clear activeModuleId if not in a workflow context that should set it
    // This prevents activeModuleId from a previous workflow context from persisting incorrectly
    else if (!urlWorkflowId && activeModuleId) { 
        // If urlWorkflowId is gone but activeModuleId is still set, clear it.
        // This handles navigation away from a workflow-specific config view.
        setActiveModuleId(null);
    }
  }, [clientId, urlWorkflowId, configId, isUsingDatabaseApproach]); // Removed activeModuleId from dependencies to avoid loop if we were to set it to null then back
  
  // Set config when module config is loaded
  useEffect(() => {
    if (moduleConfigQuery.data && activeModuleId) {
      // Extract the configuration from the response
      const configData = moduleConfigQuery.data.config_data;
      
      // Transform subtopic_mappings to handle the new format with topic_id and source tracking
      const transformedSubtopicMappings: Record<string | number, { subtopics: Record<string, string | { name: string; source_module?: string; type?: string }> }> = {};
      
      if (configData.subtopic_mappings) {
        Object.entries(configData.subtopic_mappings).forEach(([topicId, subtopicData]: [string, any]) => {
          // Handle both old and new formats
          if (subtopicData && typeof subtopicData === 'object') {
            if (subtopicData.subtopics) {
              // New format with topic_id and subtopics structure
              transformedSubtopicMappings[topicId] = {
                subtopics: subtopicData.subtopics
              };
            } else {
              // Old format where subtopicData directly contains the subtopics
              transformedSubtopicMappings[topicId] = {
                subtopics: subtopicData
              };
            }
          }
        });
      }
      
      const mappedConfig: ClientConfig = {
        config_id: configData.config_id,
        name: configData.name,
        description: configData.description,
        topic_mapping: configData.topic_mapping,
        intent_mapping: configData.intent_mapping || {},
        funnel_mapping: configData.funnel_mapping || {},
        subtopic_mappings: transformedSubtopicMappings,
        topic_validations: configData.topic_validations || {},
        prompt: configData.prompt,
        results: configData.results,
        version: configData.version,
        is_active: configData.is_active,
      };
      
      setConfig(mappedConfig);
    }
  }, [moduleConfigQuery.data, activeModuleId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePromptChange = (field: keyof PromptConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      prompt: {
        ...prev.prompt,
        [field]: value
      }
    }));
  };

  const handleResultsChange = (field: keyof ResultsConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      results: {
        ...prev.results,
        [field]: value
      }
    }));
  };

  const handleMappingChange = (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping', key: string, value: string) => {
    setConfig(prev => {
      const currentMapping = prev[mappingType] as Record<string, any>;
      const currentValue = currentMapping[key];
      
      // For topic_mapping, preserve object structure if it exists
      if (mappingType === 'topic_mapping' && currentValue && typeof currentValue === 'object' && 'source_module' in currentValue) {
        // Update only the name while preserving other properties
        return {
          ...prev,
          [mappingType]: {
            ...currentMapping,
            [key]: {
              ...currentValue,
              name: value
            }
          }
        };
      }
      
      // For simple string mappings or other mapping types
      return {
        ...prev,
        [mappingType]: {
          ...currentMapping,
          [key]: value
        }
      };
    });
  };

  const handleAddMapping = (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping') => {
    setConfig(prev => ({
      ...prev,
      [mappingType]: {
        ...(prev[mappingType] as Record<string, string>),
        [`new_key_${Object.keys(prev[mappingType] as Record<string, string>).length}`]: ''
      }
    }));
  };

  const handleRemoveMapping = (mappingType: 'topic_mapping' | 'intent_mapping' | 'funnel_mapping', key: string) => {
    setConfig(prev => {
      const newMapping = { ...(prev[mappingType] as Record<string, string>) };
      delete newMapping[key];
      return {
        ...prev,
        [mappingType]: newMapping
      };
    });
  };

  // Handler functions for subtopic operations
  const handleSubtopicChange = (topicId: string, subtopicId: string, value: string) => {
    setConfig(prev => {
      const newSubtopicMappings = { ...prev.subtopic_mappings };
      
      // Ensure the topic exists in subtopic_mappings
      if (!newSubtopicMappings[topicId]) {
        newSubtopicMappings[topicId] = { subtopics: {} };
      }
      
      // Ensure subtopics object exists
      if (!newSubtopicMappings[topicId].subtopics) {
        newSubtopicMappings[topicId].subtopics = {};
      }
      
      // Update the subtopic value
      newSubtopicMappings[topicId].subtopics[subtopicId] = value;
      
      return {
        ...prev,
        subtopic_mappings: newSubtopicMappings
      };
    });
  };

  const handleAddSubtopic = (topicId: string) => {
    setConfig(prev => {
      const newSubtopicMappings = { ...prev.subtopic_mappings };
      
      // Ensure the topic exists in subtopic_mappings
      if (!newSubtopicMappings[topicId]) {
        newSubtopicMappings[topicId] = { subtopics: {} };
      }
      
      // Ensure subtopics object exists
      if (!newSubtopicMappings[topicId].subtopics) {
        newSubtopicMappings[topicId].subtopics = {};
      }
      
      // Generate a new subtopic ID
      const existingSubtopicIds = Object.keys(newSubtopicMappings[topicId].subtopics);
      const newSubtopicId = `new_subtopic_${existingSubtopicIds.length}`;
      
      // Add the new subtopic
      newSubtopicMappings[topicId].subtopics[newSubtopicId] = '';
      
      return {
        ...prev,
        subtopic_mappings: newSubtopicMappings
      };
    });
  };

  const handleRemoveSubtopic = (topicId: string, subtopicId: string) => {
    setConfig(prev => {
      const newSubtopicMappings = { ...prev.subtopic_mappings };
      
      // Check if the topic exists
      if (newSubtopicMappings[topicId] && newSubtopicMappings[topicId].subtopics) {
        const newSubtopics = { ...newSubtopicMappings[topicId].subtopics };
        delete newSubtopics[subtopicId];
        
        newSubtopicMappings[topicId] = {
          ...newSubtopicMappings[topicId],
          subtopics: newSubtopics
        };
      }
      
      return {
        ...prev,
        subtopic_mappings: newSubtopicMappings
      };
    });
  };

  // Prompt regeneration logic based on topics and subtopics
  const generateSystemPrompt = (): string => {
    const topics = Object.values(config.topic_mapping).filter(topic => {
      const topicName = typeof topic === 'string' ? topic : topic?.name;
      return topicName && topicName.trim().length > 0;
    });
    
    const subtopics: string[] = [];
    
    // Collect all subtopics
    Object.entries(config.subtopic_mappings).forEach(([topicId, topicData]) => {
      if (topicData?.subtopics) {
        Object.values(topicData.subtopics).forEach(subtopic => {
          const subtopicName = typeof subtopic === 'string' ? subtopic : subtopic?.name;
          if (subtopicName && subtopicName.trim().length > 0) {
            subtopics.push(subtopicName);
          }
        });
      }
    });

    // Generate JSON structure for classification
    const topicFields = topics.map(topic => {
      const topicName = typeof topic === 'string' ? topic : topic?.name;
      return `"${topicName.toLowerCase().replace(/\s+/g, '_')}": true/false`;
    });

    const subtopicFields = subtopics.map(subtopic => {
      const subtopicName = typeof subtopic === 'string' ? subtopic : subtopic?.name;
      return `"${subtopicName.toLowerCase().replace(/\s+/g, '_')}": true/false`;
    });

    const allFields = [
      ...topicFields,
      ...subtopicFields,
      '"specificity_score": 1-10',
      '"commercial_score": 1-10',
      '"detected_location": "location name or null"',
      '"location_confidence": 0-10'
    ];

    return `You are an expert at classifying search queries into topics and subtopics, and detecting location information.

Analyze the given search query and return a JSON object with the following structure:
{
  ${allFields.join(',\n  ')}
}

Set the appropriate boolean values to true based on the query content. Multiple categories can be true if the query spans multiple topics.

Topic Definitions:
${topics.map((topic, index) => {
  const topicName = typeof topic === 'string' ? topic : topic?.name;
  const topicId = Object.keys(config.topic_mapping)[index];
  const relatedSubtopics = config.subtopic_mappings[topicId]?.subtopics ? 
    Object.values(config.subtopic_mappings[topicId].subtopics).map(st => typeof st === 'string' ? st : st?.name).filter(Boolean) : [];
  
  let definition = `- ${topicName}: General queries related to ${topicName.toLowerCase()}`;
  if (relatedSubtopics.length > 0) {
    definition += `\n  Subtopics: ${relatedSubtopics.join(', ')}`;
  }
  return definition;
}).join('\n')}

For location detection:
- Extract city names (e.g., "Chicago", "New York", "Seattle")
- Extract state names (e.g., "California", "Texas", "Florida")
- Extract zip codes (e.g., "60601", "10001")
- Detect "near me" or "nearby" phrases (set location as "near_me")
- Set location_confidence based on how explicit the location is:
  - 10: Explicit city/state/zip (e.g., "oil change chicago")
  - 8-9: Clear location context (e.g., "dentist 60601")
  - 5-7: Implied location (e.g., "local plumber")
  - 1-4: Vague location reference
  - 0: No location detected

Examples:
- "oil change chicago" → detected_location: "Chicago", location_confidence: 10
- "plumber near me" → detected_location: "near_me", location_confidence: 9
- "auto repair 60601" → detected_location: "60601", location_confidence: 10
- "best tire brands" → detected_location: null, location_confidence: 0`;
  };



  const handleSave = async () => {
    // If already saving, don't trigger multiple save operations
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      // Try database-first approach if enabled
      if (isUsingDatabaseApproach) {
        try {
          // If we have an active module ID, use the module config save endpoint
          if (activeModuleId) {
            console.log(`Saving config to module ID ${activeModuleId}`);
            
            const moduleConfigData = {
              config_id: config.config_id,
              name: config.name,
              description: config.description,
              topic_mapping: config.topic_mapping,
              intent_mapping: config.intent_mapping,
              funnel_mapping: config.funnel_mapping,
              subtopic_mappings: config.subtopic_mappings, 
              topic_validations: config.topic_validations,
              prompt: config.prompt,
              results: config.results,
              version: config.version,
              is_active: config.is_active
            };
            
            const result: DbModuleConfigResponse = await saveModuleConfigDbMutation.mutateAsync({
              moduleId: activeModuleId,
              config: moduleConfigData
            });

            // After successfully saving module config, update dbConfigId state
            // The 'result.id' from DbModuleConfigResponse should be the numeric ID of the ModuleConfigs record
            if (result && typeof result.id === 'number') {
              setDbConfigId(result.id);
              // Also update localStorage for consistency, using the textual config_id from form/state as key
              // This helps if the user navigates away and comes back via a non-workflow route to this config.
              localStorage.setItem(`module4_${config.config_id}_db_config_id`, result.id.toString());
              console.log(`Set dbConfigId to ${result.id} after saving module config for module ${activeModuleId}`);
            }
            
            setSnackbar({
              open: true,
              message: 'Module configuration saved successfully',
              severity: 'success'
            });
            
            return;
          }
          
          // Regular config save flow (no active module ID)
          // Must have a client ID for database operations
          let clientIdToUse = dbClientId;
          
          if (!clientIdToUse) {
            // Try to get client by code
            console.log('No database client ID found, looking up client...');
            try {
              const clients = await workflowService.getDbClients();
              // Assuming the config_id is a client code or contains it
              const clientCode = config.config_id.split('-')[0] || config.config_id;
              const client = clients.find(c => c.client_code === clientCode);
              
              if (client) {
                clientIdToUse = client.client_id;
                setDbClientId(clientIdToUse);
                localStorage.setItem('db_client_id', clientIdToUse.toString());
              } else {
                throw new Error(`No matching client found for code: ${clientCode}`);
              }
            } catch (err) {
              console.error('Error finding client:', err);
              throw new Error('Failed to find client ID for database operations');
            }
          }
          
          // Now convert the config to database format
          const dbConfigRequest = {
            clientId: clientIdToUse,
            name: config.name,
            description: config.description,
            topicMapping: config.topic_mapping,
            intentMapping: config.intent_mapping,
            funnelMapping: config.funnel_mapping,
            subtopicMappings: config.subtopic_mappings,
            topicValidations: config.topic_validations,
            prompt: config.prompt,
            results: config.results,
            isActive: config.is_active
          };
          
          let result;
          
          // Create or update based on configId and dbConfigId
          if (configId === 'new') {
            // Create new config
            result = await createConfigDbMutation.mutateAsync(dbConfigRequest);
            
            if (result && result.configId) {
              setDbConfigId(result.configId);
              localStorage.setItem(`module4_${config.config_id}_db_config_id`, result.configId.toString());
            }
          } else if (dbConfigId) {
            // Update existing config
            result = await updateConfigDbMutation.mutateAsync({
              configId: dbConfigId,
              config: dbConfigRequest
            });
          } else {
            // We have a configId but no dbConfigId, create a new DB config
            result = await createConfigDbMutation.mutateAsync(dbConfigRequest);
            
            if (result && result.configId) {
              setDbConfigId(result.configId);
              localStorage.setItem(`module4_${configId}_db_config_id`, result.configId.toString());
            }
          }
          
          setSnackbar({
            open: true,
            message: configId === 'new' ? 
              'Batch keyword classification created successfully' : 
              'Batch keyword classification updated successfully',
            severity: 'success'
          });
          
          // Navigate back to the list view after a short delay
          // Add a longer delay to allow database connections to close
          setTimeout(() => {
            navigate('/module4/configs');
          }, 2500);
          
          return;
        } catch (dbError) {
          console.error('Database-first approach failed:', dbError);
          // Fall through to standard approach
        }
      }
      
      // Standard blob-first approach
      const module4Service = Module4Service.getInstance();
      
      if (configId === 'new') {
        await module4Service.createConfig(config);
        setSnackbar({
          open: true,
          message: 'Batch keyword classification created successfully',
          severity: 'success'
        });
      } else if (configId) {
        await module4Service.updateConfig(configId, config);
        setSnackbar({
          open: true,
          message: 'Batch keyword classification updated successfully',
          severity: 'success'
        });
      }
      
      // Navigate back to the list view after a short delay
      setTimeout(() => {
        navigate('/module4/configs');
      }, 1500);
    } catch (error) {
      console.error('Error saving batch keyword classification:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save batch keyword classification',
        severity: 'error'
      });
    } finally {
      // Add a delay before allowing another save - prevents rapid consecutive saves
      setTimeout(() => {
        setIsSaving(false);
      }, 1000);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAssistantConfig = (generatedConfig: ClientConfig) => {
    setConfig(generatedConfig);
    setShowAssistant(false);
    setSnackbar({
      open: true,
      message: 'Batch keyword classification generated successfully. Review and save when ready.',
      severity: 'success'
    });
  };

  // Handler for starting the batch process
  const handleStartBatchProcess = async () => {
    // Set processing state immediately for responsive UI
    setIsProcessing(true);
    setBatchError(null);
    setBatchSuccessMessage(null);
    
    let currentDbConfigId = dbConfigId; // Use current state value
    
    try {
      // Auto-save configuration if not saved to database before starting batch process
      if (!currentDbConfigId && !isSaving && isConfigValid()) {
        console.log('Auto-saving configuration to database before starting batch process...');
        await handleSave();
        console.log('Configuration auto-saved successfully');
        
        // Get the updated dbConfigId from localStorage since state update is async
        const savedConfigId = localStorage.getItem(`module4_${config.config_id}_db_config_id`);
        currentDbConfigId = savedConfigId ? parseInt(savedConfigId, 10) : null;
        console.log('Retrieved dbConfigId from localStorage after save:', currentDbConfigId);
      }
    } catch (error) {
      console.error('Error auto-saving configuration before batch process:', error);
      setBatchError('Failed to save configuration before starting batch process. Please save manually and try again.');
      setIsProcessing(false); // Reset processing state on error
      return; // Exit early if save fails
    }
    
    let jobIdForLastStarted: string | number | null = null;
    let jobTypeForLastStarted: string = '';
    let navigationTargetUrl: string | null = null;

    try {
      const numericWorkflowId = urlWorkflowId ? parseInt(urlWorkflowId, 10) : null;

      // Determine the client identifier to use for path construction.
      // Prefer clientId from URL if available (current workflow context), otherwise use cached dbClientId
      let clientIdForPath: string | number | null = null;
      if (clientId) { // clientId from URL params (current workflow context) - this should take priority
        clientIdForPath = clientId;
        console.log(`Using client ID '${clientId}' from URL for current workflow context`);
      } else if (dbClientId) { // dbClientId is numeric and comes from loaded/saved config (fallback)
        clientIdForPath = dbClientId;
        console.log(`Using cached dbClientId '${dbClientId}' as fallback`);
      }

      if (currentDbConfigId && numericWorkflowId && inputFilePath && clientIdForPath) {
        // inputFilePath now contains the full normalized path from the dropdown
        let fullInputBlobPath = inputFilePath;
        
        // If inputFilePath is just a filename (backwards compatibility), construct the full path
        if (!inputFilePath.includes('/')) {
          fullInputBlobPath = `client-data/${clientIdForPath}/workflows/${numericWorkflowId}/module2/output/${inputFilePath}`;
          console.log(`Constructed full input blob path from filename: ${fullInputBlobPath}`);
        } else {
          console.log(`Using pre-normalized input blob path: ${fullInputBlobPath}`);
        }

        const request: DbBatchProcessRequest = {
          workflowId: numericWorkflowId,
          inputFile: fullInputBlobPath, // Use the normalized full path
          configId: currentDbConfigId,
          batchSize: batchSize,
          priority: priority,
        };

        const module4Service = Module4Service.getInstance();
        const result: DbBatchProcessResponse = await module4Service.startBatchProcessDb(request);
        
        // Determine primary job identifier and navigation path
        if (result && typeof result.moduleId === 'number' && result.id && typeof result.workflowId === 'number') {
          jobIdForLastStarted = result.id; // Store the UUID as the primary job ID
          jobTypeForLastStarted = 'db';
          navigationTargetUrl = `/jobs/${result.id}?moduleId=${result.moduleId}&workflowId=${result.workflowId}&clientId=${clientIdForPath}`;
          
          console.log(`DB job started. UUID: ${result.id}, Job Module ID: ${result.moduleId}, Workflow ID: ${result.workflowId}. Navigating to: ${navigationTargetUrl}`);
          setBatchSuccessMessage(`Batch process started successfully. Job ID: ${result.id}`);

        } else if (result && typeof result.moduleId === 'number') {
          // This block is a fallback if result.id or result.workflowId was missing from the preferred path above.
          // This path leads to /jobs/db-NUMERIC_MODULE_ID style navigation.
          jobIdForLastStarted = result.moduleId; 
          jobTypeForLastStarted = 'db';
          navigationTargetUrl = `/jobs/db-${result.moduleId}`;
          if (typeof result.workflowId === 'number') { 
            localStorage.setItem(`module4_workflow_${result.workflowId}_module_${result.moduleId}_context`, "active");
            localStorage.setItem(`module4_last_started_job_module_id_for_workflow_${result.workflowId}`, result.moduleId.toString());
          }
          console.log(`DB job started (fallback to numeric moduleId nav). Primary ID (numeric module): ${jobIdForLastStarted}. Navigating to: ${navigationTargetUrl}`);
          setBatchSuccessMessage(`Batch process started successfully with Module ID: ${result.moduleId}`);

        } else if (result && result.id) { 
          // Fallback if only UUID is available from the result.
          jobIdForLastStarted = result.id;
          jobTypeForLastStarted = 'db';
          navigationTargetUrl = `/jobs/${result.id}`;
          console.warn(`DB job started with UUID only: ${jobIdForLastStarted}. ModuleID/WorkflowID might be missing in API response. Navigating to: ${navigationTargetUrl}`);
          setBatchSuccessMessage(`Batch process started successfully with Job ID: ${result.id}`);
        } else {
          // Construct a more informative error message for missing prerequisites
          let missingInfo = [];
          if (!currentDbConfigId) missingInfo.push("database config ID (dbConfigId)");
          if (!numericWorkflowId) missingInfo.push("numeric workflow ID (from urlWorkflowId)");
          if (!inputFilePath) missingInfo.push("input file selection (inputFilePath)");
          if (!clientIdForPath) missingInfo.push("client identifier for path (dbClientId or clientId from URL)");
          
          const errorMsg = `Cannot start batch process. Prerequisites missing: ${missingInfo.join('; ')}.`;
          setBatchError(errorMsg);
          console.error(errorMsg, { dbConfigId: currentDbConfigId, numericWorkflowId, inputFilePath, clientIdForPath, dbClientId, clientIdFromParams: clientId });
        }
        
        // Store details for 'last_started_job' for other parts of the app to reference
        if (jobIdForLastStarted !== null) {
            localStorage.setItem('last_started_job_type', jobTypeForLastStarted);
            localStorage.setItem('last_started_job_id', jobIdForLastStarted.toString()); // Ensure it's a string
        }

        // Navigate if a target URL was determined
        if (navigationTargetUrl) {
          // Consider using formatJobIdForRoute if more complex formatting is ever needed
          // For now, direct navigation is fine as paths are simple.
          navigate(navigationTargetUrl);
        }

      } else {
        let missingInfo = [];
        if (!currentDbConfigId) missingInfo.push("database config ID (dbConfigId)");
        if (!numericWorkflowId) missingInfo.push("numeric workflow ID (from urlWorkflowId)");
        if (!inputFilePath) missingInfo.push("input file selection (inputFilePath)");
        if (!clientIdForPath) missingInfo.push("client identifier for path (dbClientId or clientId from URL)");
        
        const errorMsg = `Cannot start batch process. Prerequisites missing: ${missingInfo.join('; ')}.`;
        setBatchError(errorMsg);
        console.error(errorMsg, { dbConfigId: currentDbConfigId, numericWorkflowId, inputFilePath, clientIdForPath, dbClientId, clientIdFromParams: clientId });
      }
    } catch (error: any) {
      console.error('Error starting batch process:', error);
      setBatchError(error instanceof Error ? error.message : String(error));
      setBatchSuccessMessage(null); // Clear success message on error
    } finally {
      setIsProcessing(false);
    }
  };

  // Add effect to auto-select the latest relevant.csv file
  useEffect(() => {
    if (!isLoadingCsvFiles && csvFiles.length > 0 && !inputFilePath) {
      // Find files with "relevant" in the name from Module 2
      const relevantFiles = csvFiles
        .filter(file => {
          const fileName = file.name.toLowerCase();
          return fileName.includes('relevant') && fileName.endsWith('.csv');
        })
        .sort((a, b) => b.name.localeCompare(a.name));
      
      if (relevantFiles.length > 0) {
        // Select the most recent relevant.csv file
        setInputFilePath(relevantFiles[0].path);
        console.log("Auto-selected latest relevant CSV file:", relevantFiles[0]);
      }
    }
  }, [csvFiles, isLoadingCsvFiles, inputFilePath]);

  // Add useEffect to fetch CSV files when component mounts and when relevant parameters change
  useEffect(() => {
    console.log("Effect triggered. clientId:", clientId, "urlWorkflowId:", urlWorkflowId, "activeTab:", activeTab);
    
    // Try to get values even if params are not available
    const clientIdParam = clientId || (config.config_id ? config.config_id.split('-')[0] : '');
    const workflowIdParam = urlWorkflowId || '';
    
    // Log available values
    console.log("Using clientIdParam:", clientIdParam, "workflowIdParam:", workflowIdParam);
    
    // Only fetch if we have some values to work with, and we're on the Process tab
    if ((clientIdParam || workflowIdParam) && activeTab === 4) {
      console.log("Attempting to fetch CSV files...");
      fetchCsvFiles();
    }
  }, [clientId, urlWorkflowId, config.config_id, activeTab]);

  const handleStepperCollapseChange = (collapsed: boolean) => {
    setStepperCollapsed(collapsed);
    localStorage.setItem('module4_stepper_collapsed', collapsed.toString());
  };

  // Check if the config is valid for saving
  const isConfigValid = () => {
    return (
      config.config_id && 
      config.name && 
      config.prompt.system_prompt && 
      config.results.output_format
    );
  };

  // Check if all required configuration is complete to run the process
  const isReadyToProcess = () => {
    return (
      isConfigValid() && 
      Object.keys(config.topic_mapping).length > 0 &&
      config.prompt.system_prompt.length > 0 &&
      config.results.output_format.length > 0
    );
  };

  return (
    <Box className="container mx-auto py-6">
      {/* Main layout container - always visible */}
      <Grid container spacing={2} columnGap={!stepperCollapsed ? 4 : 0}>
        {/* Workflow Stepper - always visible */}
        <Grid item xs="auto">
          <WorkflowStepper
            activeStep={3}
            workflowId={urlWorkflowId}
            clientId={clientId}
            moduleCompletionStatus={getWorkflowStatus(urlWorkflowId)}
            onCollapseChange={handleStepperCollapseChange}
          />
        </Grid>
        
        {/* Main content area */}
        <Grid 
          item 
          xs
          sx={{
            pl: stepperCollapsed ? 1 : 3  // More left padding when stepper is open
          }}
        >
          {/* Breadcrumb navigation with back button */}
          {clientId && urlWorkflowId && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Breadcrumbs 
                aria-label="breadcrumb" 
                sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
              >
                <Link 
                  color="inherit" 
                  href="/" 
                  onClick={(e) => { e.preventDefault(); navigate('/'); }}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                  Dashboard
                </Link>
                <Typography color="text.secondary">
                  {clientData?.name || `Client ${clientId}`} ({clientId} - {urlWorkflowId})
                </Typography>
                <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                  Module 4: Batch Keyword Classification
                </Typography>
              </Breadcrumbs>
              
              <MuiButton
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/start-process')}
                sx={{ ml: 2 }}
              >
                Start Process
              </MuiButton>
            </Box>
          )}
          {/* Show loading overlay for just the main content when isLoading is true */}
          <Box sx={{ position: 'relative' }}>
            {isLoading && (
              <Box 
                sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                  zIndex: 10,
                  borderRadius: 1
                }}
              >
                <CircularProgress size={60} />
              </Box>
            )}
            
            {/* Main content Paper - becomes semi-transparent during loading */}
            <Paper 
              sx={{ 
                p: 3, 
                opacity: isLoading ? 0.6 : 1,
                transition: 'opacity 0.3s ease',
                pointerEvents: isLoading ? 'none' : 'auto'
              }}
            >
              {/* Section title - updated to be more general with space for future additions */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">
                  Configuration
                </Typography>
                {/* Reserved space for future Process Results section */}
                <Box>
                  {/* This space can be used later for processed results indicators or actions */}
                </Box>
              </Box>

              {/* Enhanced step navigation with integrated progress indicators - updated styling */}
              <TabNavigation
                config={config}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isReadyToProcess={isReadyToProcess}
              />

              {/* Content panels - keep existing TabPanel components */}
              <TabPanelBasicInfo
                config={config}
                activeTab={activeTab}
                handleInputChange={handleInputChange}
                configId={configId || ''}
              />
              
              <TabPanelTopics
                config={config}
                activeTab={activeTab}
                handleMappingChange={handleMappingChange}
                handleAddMapping={handleAddMapping}
                handleRemoveMapping={handleRemoveMapping}
                handleSubtopicChange={handleSubtopicChange}
                handleAddSubtopic={handleAddSubtopic}
                handleRemoveSubtopic={handleRemoveSubtopic}
              />
              
              <TabPanelPrompt
                config={config}
                activeTab={activeTab}
                handlePromptChange={handlePromptChange}
              />
              
              <TabPanelResults
                config={config}
                activeTab={activeTab}
                handleResultsChange={handleResultsChange}
              />
              
              <TabPanelProcess
                activeTab={activeTab}
                inputFilePath={inputFilePath}
                setInputFilePath={setInputFilePath}
                csvFiles={csvFiles}
                isLoadingCsvFiles={isLoadingCsvFiles}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                batchSize={batchSize}
                setBatchSize={setBatchSize}
                priority={priority}
                setPriority={setPriority}
                fetchCsvFiles={fetchCsvFiles}
              />
              
              {/* Replace the floating navigation bar with the new component */}
              <NavigationBar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isConfigValid={isConfigValid}
                isSaving={isSaving}
                handleSave={handleSave}
                isProcessing={isProcessing}
                isReadyToProcess={isReadyToProcess}
                inputFilePath={inputFilePath}
                handleStartBatchProcess={handleStartBatchProcess}
                selectedModel={selectedModel}
              />
            </Paper>
          </Box>
          
          {/* Dialog and Snackbar are outside the loading state */}
          <Dialog
            open={showAssistant}
            onClose={() => setShowAssistant(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>AI Batch Keyword Classification Assistant</DialogTitle>
            <DialogContent>
              <Module4ConfigAssistant
                onConfigGenerated={handleAssistantConfig}
                initialPrompt={config.description}
              />
            </DialogContent>
            <DialogActions>
              <MuiButton onClick={() => setShowAssistant(false)}>Cancel</MuiButton>
            </DialogActions>
          </Dialog>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Module4Config;
